class MCHeader extends HTMLElement {
  connectedCallback() {
    this.drawer = this.querySelector('[data-menu-drawer]');
    this.drawerViewport = this.querySelector('[data-drawer-viewport]');
    this.openButton = this.querySelector('[data-menu-open]');
    this.closeButtons = this.querySelectorAll('[data-menu-close]');
    this.drawerLinks = this.querySelectorAll('a.mc-header__drawer-link[href]');
    this.drawerViewTriggers = this.querySelectorAll('[data-drawer-view-open]');
    this.drawerViewBackButtons = this.querySelectorAll('[data-drawer-view-back]');
    this.menuDetails = this.querySelectorAll('.mc-header__nav-item details, .mc-header__submenu-details');
    this.drawerDuration = 420;
    this.viewDuration = 380;
    this.viewStack = ['root'];

    if (!this.drawer || !this.openButton) return;

    this.openButton.addEventListener('click', this.openMenu);
    this.closeButtons.forEach((button) => {
      button.addEventListener('click', this.closeMenu);
    });
    this.drawerLinks.forEach((link) => {
      link.addEventListener('click', this.closeMenu);
    });
    this.drawerViewTriggers.forEach((trigger) => {
      trigger.addEventListener('click', this.onDrawerViewOpen);
    });
    this.drawerViewBackButtons.forEach((button) => {
      button.addEventListener('click', this.onDrawerViewBack);
    });
    this.menuDetails.forEach((details) => {
      details.addEventListener('toggle', this.onDetailsToggle);
    });
    this.drawer.addEventListener('keyup', this.onDrawerKeyUp);
    this.drawer.addEventListener('click', this.onBackdropClick);
    this.drawer.addEventListener('cancel', this.onCancel);
  }

  disconnectedCallback() {
    if (!this.drawer || !this.openButton) return;

    window.clearTimeout(this.closeTimer);
    this.openButton.removeEventListener('click', this.openMenu);
    this.closeButtons.forEach((button) => {
      button.removeEventListener('click', this.closeMenu);
    });
    this.drawerLinks?.forEach((link) => {
      link.removeEventListener('click', this.closeMenu);
    });
    this.drawerViewTriggers?.forEach((trigger) => {
      trigger.removeEventListener('click', this.onDrawerViewOpen);
    });
    this.drawerViewBackButtons?.forEach((button) => {
      button.removeEventListener('click', this.onDrawerViewBack);
    });
    this.menuDetails?.forEach((details) => {
      details.removeEventListener('toggle', this.onDetailsToggle);
    });
    this.drawer.removeEventListener('keyup', this.onDrawerKeyUp);
    this.drawer.removeEventListener('click', this.onBackdropClick);
    this.drawer.removeEventListener('cancel', this.onCancel);
  }

  getDrawerView(viewId) {
    return this.drawer?.querySelector(`[data-drawer-view="${viewId}"]`);
  }

  getActiveDrawerView() {
    return this.drawerViewport?.querySelector('.mc-header__drawer-view.is-active');
  }

  onDetailsToggle = (event) => {
    const details = event.currentTarget;
    const summary = details.querySelector(':scope > summary');
    if (!summary) return;
    summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
  };

  onDrawerViewOpen = (event) => {
    const trigger = event.currentTarget;
    const viewId = trigger.getAttribute('data-drawer-view-open');
    if (!viewId) return;

    this.openDrawerView(viewId, trigger);
  };

  onDrawerViewBack = () => {
    this.closeDrawerView();
  };

  openDrawerView(viewId, trigger) {
    const nextView = this.getDrawerView(viewId);
    const currentView = this.getActiveDrawerView();
    if (!nextView || !currentView || currentView === nextView) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    nextView.hidden = false;
    nextView.scrollTop = 0;
    this.drawerViewport?.classList.add('is-overlay-active');

    window.requestAnimationFrame(() => {
      currentView.classList.remove('is-active');
      currentView.classList.add('is-behind');
      nextView.classList.add('is-active');
      trigger?.setAttribute('aria-expanded', 'true');
    });

    if (!reducedMotion) {
      currentView.classList.add('is-animating');
      nextView.classList.add('is-animating');
      window.setTimeout(() => {
        currentView.classList.remove('is-animating');
        nextView.classList.remove('is-animating');
      }, this.viewDuration);
    }

    this.viewStack.push(viewId);
  }

  closeDrawerView() {
    if (this.viewStack.length <= 1) return;

    const currentViewId = this.viewStack.pop();
    const previousViewId = this.viewStack[this.viewStack.length - 1];
    const currentView = this.getDrawerView(currentViewId);
    const previousView = this.getDrawerView(previousViewId);
    if (!currentView || !previousView) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const closingTrigger = this.drawer?.querySelector(`[data-drawer-view-open="${currentViewId}"]`);

    currentView.classList.remove('is-active');
    previousView.classList.remove('is-behind');
    previousView.classList.add('is-active');
    closingTrigger?.setAttribute('aria-expanded', 'false');

    if (this.viewStack.length === 1) {
      this.drawerViewport?.classList.remove('is-overlay-active');
    }

    const hideCurrentView = () => {
      currentView.hidden = true;
      currentView.classList.remove('is-animating');
      previousView.classList.remove('is-animating');
    };

    if (reducedMotion) {
      hideCurrentView();
      return;
    }

    currentView.classList.add('is-animating');
    window.setTimeout(hideCurrentView, this.viewDuration);
  }

  resetDrawerViews() {
    this.viewStack = ['root'];

    this.drawer?.querySelectorAll('[data-drawer-view]').forEach((view) => {
      const isRoot = view.dataset.drawerView === 'root';
      view.hidden = !isRoot;
      view.classList.toggle('is-active', isRoot);
      view.classList.remove('is-behind', 'is-animating');
      view.scrollTop = 0;
    });

    this.drawer?.querySelectorAll('[data-drawer-view-open]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });

    this.drawerViewport?.classList.remove('is-overlay-active');
  }

  openMenu = () => {
    if (!this.drawer.open) {
      this.drawer.classList.remove('is-closing');
      this.drawer.showModal();
      document.body.classList.add('overflow-hidden');
      this.openButton.setAttribute('aria-expanded', 'true');
      this.closest('.section-header')?.classList.add('menu-open');
    }
  };

  closeMenu = () => {
    if (!this.drawer.open || this.drawer.classList.contains('is-closing')) return;

    const duration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : this.drawerDuration;

    this.drawer.classList.add('is-closing');
    this.openButton.setAttribute('aria-expanded', 'false');

    window.clearTimeout(this.closeTimer);

    if (duration === 0) {
      this.finishClose();
      return;
    }

    this.closeTimer = window.setTimeout(() => {
      this.finishClose();
    }, duration);
  };

  finishClose = () => {
    if (!this.drawer.open) return;

    this.drawer.classList.remove('is-closing');
    this.drawer.close();
    document.body.classList.remove('overflow-hidden');
    this.closest('.section-header')?.classList.remove('menu-open');
    this.resetDrawerViews();
  };

  onDrawerKeyUp = (event) => {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    if (this.viewStack.length > 1) {
      event.preventDefault();
      this.closeDrawerView();
      return;
    }

    this.closeMenu();
  };

  onCancel = (event) => {
    event.preventDefault();

    if (this.viewStack.length > 1) {
      this.closeDrawerView();
      return;
    }

    this.closeMenu();
  };

  onBackdropClick = (event) => {
    const rect = this.drawer.getBoundingClientRect();
    const clickedInDialog =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;

    if (!clickedInDialog) {
      this.closeMenu();
    }
  };
}

if (!customElements.get('mc-header')) {
  customElements.define('mc-header', MCHeader);
}
